import { ApiInstance, RetrySettings } from '@src/services/api-instance';
import { getAuthToken } from '@src/services/auth-service';
import { GetTasksResponseDTO, TaskItemDTO } from './dto/brain-tasks.get.response.dto';
import { PutTasksBodyDTO } from './dto/brain-tasks.putById.body.dto';
import { GetByIdTaskDTO } from './dto/brain-tasks.getById.response.dto';
import { GetTasksSummaryResponseDTO } from './dto/brain-tasks.getSummary.response.dto';
import { PutBulkTasksBodyDTO } from './dto/brain-tasks.putBulk.body.dto';
import { TaskCommentDTO } from './dto/brain-tasks-comments.response.dto';
import { PostTaskCommentBodyDTO } from './dto/brain-tasks-comments.post.body.dto';
import { PutTaskCommentBodyDTO } from './dto/brain-tasks-comments.put.body.dto';
import { GetTasksGroupsResponseDTO } from './dto/brain-tasks.getGroups.response.dto';
import { GetTasksAnalyticsResponseDTO, TAnalyticsGranularity } from './dto/brain-tasks.getAnalytics.response.dto';

export class BrainTasksService {
  private readonly client: ApiInstance;

  constructor(client: ApiInstance) {
    this.client = new ApiInstance(`${client.getBaseUrl()}/tasks`, client.getTimeout());
  }

  async get(request: {
    params: {
      currentPage?: number;
      limit?: number;
      sortBy?: 'createdAt' | 'completedAt';
      sortOrder?: 'asc' | 'desc';
      message?: string;
      startDate?: string;
      endDate?: string;
      updateStartDate?: string;
      updateEndDate?: string;
      status?: string[];
      source?: string[];
      type?: string[];
      assignedUserId?: string;
      organizationId?: string;
      requestId?: string;
    };
    settings?: RetrySettings;
  }): Promise<GetTasksResponseDTO> {
    return (
      await this.client.get<GetTasksResponseDTO>(``, request.params, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }

  async getGroups(request: {
    params: {
      currentPage?: number;
      limit?: number;
      sortBy?: 'createdAt' | 'completedAt';
      sortOrder?: 'asc' | 'desc';
      message?: string;
      startDate?: string;
      endDate?: string;
      updateStartDate?: string;
      updateEndDate?: string;
      status?: string[];
      source?: string[];
      type?: string[];
      assignedUserId?: string;
      organizationId?: string;
    };
    settings?: RetrySettings;
  }): Promise<GetTasksGroupsResponseDTO> {
    return (
      await this.client.get<GetTasksGroupsResponseDTO>(`groups`, request.params, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }

  async getById(request: { id: string; settings?: RetrySettings }): Promise<GetByIdTaskDTO> {
    return (
      await this.client.get<GetByIdTaskDTO>(
        `${request.id}`,
        {},
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async putById(request: { id: string; body: PutTasksBodyDTO; settings?: RetrySettings }): Promise<TaskItemDTO> {
    return (
      await this.client.put<PutTasksBodyDTO, TaskItemDTO>(`${request.id}`, {}, request.body, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }

  async getSummary(request: {
    params: {
      message?: string;
      startDate?: string;
      endDate?: string;
      updateStartDate?: string;
      updateEndDate?: string;
      status?: string[];
      source?: string[];
      type?: string[];
      assignedUserId?: string;
      organizationId?: string;
    };
    settings?: RetrySettings;
  }): Promise<GetTasksSummaryResponseDTO> {
    return (
      await this.client.get<GetTasksSummaryResponseDTO>(`summary`, request.params, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }

  async getAnalytics(request: {
    params: {
      startDate?: string;
      endDate?: string;
      granularity?: TAnalyticsGranularity;
      timezone?: string;
      source?: string[];
      type?: string[];
      organizationId?: string;
    };
    settings?: RetrySettings;
  }): Promise<GetTasksAnalyticsResponseDTO> {
    return (
      await this.client.get<GetTasksAnalyticsResponseDTO>(`analytics`, request.params, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }

  async putBulk(request: { body: PutBulkTasksBodyDTO; settings?: RetrySettings }): Promise<void> {
    await this.client.put<PutBulkTasksBodyDTO, void>(`bulk`, {}, request.body, {
      bearerToken: await getAuthToken(),
      settings: request.settings,
    });
  }

  async postComment(request: {
    taskId: string;
    body: PostTaskCommentBodyDTO;
    settings?: RetrySettings;
  }): Promise<TaskCommentDTO> {
    return (
      await this.client.post<PostTaskCommentBodyDTO, TaskCommentDTO>(`${request.taskId}/comments`, request.body, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }

  async putComment(request: {
    taskId: string;
    commentId: string;
    body: PutTaskCommentBodyDTO;
    settings?: RetrySettings;
  }): Promise<TaskCommentDTO> {
    return (
      await this.client.put<PutTaskCommentBodyDTO, TaskCommentDTO>(
        `${request.taskId}/comments/${request.commentId}`,
        {},
        request.body,
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async deleteComment(request: { taskId: string; commentId: string; settings?: RetrySettings }): Promise<void> {
    await this.client.delete<void>(
      `${request.taskId}/comments/${request.commentId}`,
      {},
      {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      },
    );
  }

  async postRerun(request: { id: string; settings?: RetrySettings }): Promise<{ message: string }> {
    return (
      await this.client.post<void, { message: string }>(`${request.id}/reruns`, undefined, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }
}
